import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, Loader2, Download, AlertCircle, Trash2, Info, Eye, Share2, FileText } from "lucide-react";
import JSZip from "jszip";
import { api, getToken } from "../api.js";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.js";
import { Button } from "../components/ui/button.js";
import { Badge } from "../components/ui/badge.js";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "../components/ui/table.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.js";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip.js";
import ClaveSiiModal, { type SiiCredentials } from "../components/ClaveSiiModal.js";
import CompartirModal, { type CompartirDocumentoData } from "../components/CompartirModal.js";

interface Documento {
  id: number;
  tipoDte: number;
  folio: number | null;
  estado: string;
  receptorRut: string | null;
  receptorNombre: string | null;
  tipoRetencion: number | null;
  montoNeto?: number;
  montoIva?: number;
  montoTotal: number;
  fechaEmision: string;
  fechaProcesado: string | null;
  errorMensaje: string | null;
  documentoRefId: number | null;
  rutEmisor: string;
  userId: number;
  descripcionServicio?: string | null;
  shareHash?: string;
  notaCredito?: { id: number; folio: number; shareHash?: string } | null;
  documentoRef?: { id: number; folio: number; tipoDte: number; shareHash?: string } | null;
}

const TAB_CONFIG = [
  { value: "bhe", label: "BHE", tipos: "69" },
  { value: "be", label: "Boleta Electrónica", tipos: "39,41" },
  { value: "factura", label: "Factura", tipos: "33,34" },
  { value: "nota-credito", label: "Nota de Crédito", tipos: "61" },
] as const;

function tipoLabel(tipoDte: number): string {
  switch (tipoDte) {
    case 69: return "BHE";
    case 39: return "BE Afecta";
    case 41: return "BE Exenta";
    case 33: return "Factura";
    case 34: return "Factura Exenta";
    case 61: return "Nota de Crédito";
    default: return `DTE ${tipoDte}`;
  }
}

function tipoBadge(tipoDte: number) {
  switch (tipoDte) {
    case 69:
      return <Badge variant="outline" className="font-mono text-[10px]">BHE (69)</Badge>;
    case 39:
      return <Badge variant="outline" className="font-mono text-[10px] text-blue-600 border-blue-300 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20">BE Afecta (39)</Badge>;
    case 41:
      return <Badge variant="outline" className="font-mono text-[10px] text-emerald-600 border-emerald-300 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20">BE Exenta (41)</Badge>;
    case 33:
      return <Badge variant="outline" className="font-mono text-[10px] text-blue-600 border-blue-300 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20">Afecta (33)</Badge>;
    case 34:
      return <Badge variant="outline" className="font-mono text-[10px] text-emerald-600 border-emerald-300 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20">Exenta (34)</Badge>;
    case 61:
      return <Badge variant="outline" className="font-mono text-[10px] text-purple-600 border-purple-300 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/20">NC (61)</Badge>;
    default:
      return <Badge variant="outline" className="font-mono text-[10px]">{tipoDte}</Badge>;
  }
}

function isBheAnulable(fechaEmision: string, tipoDte: number): boolean {
  if (tipoDte !== 69) return false;
  const hoy = new Date();
  const emision = new Date(fechaEmision);
  if (isNaN(emision.getTime())) return false;
  const plazo = new Date(emision.getFullYear(), emision.getMonth() + 1, 10, 23, 59, 59);
  return hoy <= plazo;
}

function estadoBadge(doc: Documento) {
  switch (doc.estado) {
    case "EMITIDO":
      return <Badge variant="success">Emitido</Badge>;
    case "EMITIDO_SIN_PDF":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
          <AlertCircle className="h-3 w-3 text-amber-600" />
          Sin PDF oficial
        </span>
      );
    case "EMITIDO_SIN_HTML":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
          <AlertCircle className="h-3 w-3 text-amber-600" />
          Sin descarga
        </span>
      );
    case "ERROR":
      return (
        <span
          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200 shadow-xs"
          title={doc.errorMensaje || "Error en proceso"}
        >
          <AlertCircle className="h-3 w-3 text-amber-700 dark:text-amber-400" />
          Error
        </span>
      );
    case "ANULADO": {
      const isManual = doc.errorMensaje?.toLowerCase().includes("externamente") || doc.errorMensaje?.toLowerCase().includes("manual");
      if (isManual) {
        return (
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300"
            title={doc.errorMensaje || "Anulada manualmente fuera de FolioListo"}
          >
            Anulada manual
          </span>
        );
      }
      const labelAnulada = doc.notaCredito?.folio
        ? `Anulada (NC #${doc.notaCredito.folio})`
        : doc.tipoDte === 69 ? "Anulada" : "Anulada (NC)";
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300">
          {labelAnulada}
        </span>
      );
    }
    default:
      return <Badge variant="secondary">{doc.estado}</Badge>;
  }
}

function retencionLabel(tipoRetencion: number | null): string {
  if (tipoRetencion === 1) return "Receptor";
  if (tipoRetencion === 2) return "Emisor";
  return "—";
}

export default function EmitidosPage() {
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState<string>("bhe");
  const [anularLoading, setAnularLoading] = useState(false);
  const [claveModalOpen, setClaveModalOpen] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [pendingAction, setPendingAction] = useState<{ type: "anular" | "reintentar"; ids: number[] } | null>(null);
  const [shareDoc, setShareDoc] = useState<CompartirDocumentoData | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    const tabCfg = TAB_CONFIG.find((t) => t.value === activeTab);
    const params = new URLSearchParams();
    if (tabCfg) params.set("tipoDte", tabCfg.tipos);
    api<Documento[]>(`/documentos?${params.toString()}`)
      .then(setDocumentos)
      .catch(() => toast.error("Error al cargar documentos"))
      .finally(() => setLoading(false));
  }, [activeTab]);

  useEffect(() => {
    setSelected(new Set());
    load();
  }, [load]);

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === documentos.length) setSelected(new Set());
    else setSelected(new Set(documentos.map((d) => d.id)));
  };

  const reintentarHtml = async (id: number, creds?: SiiCredentials) => {
    if (!creds) {
      setPendingAction({ type: "reintentar", ids: [id] });
      setClaveModalOpen(true);
      return;
    }
    setActionLoading(id);
    try {
      const res = await api<Record<string, unknown>>(`/documentos/${id}/reintentar-html`, {
        method: "POST",
        body: JSON.stringify({ rutSii: creds.rutSii, claveSii: creds.claveSii }),
      });
      if (res.success) toast.success("HTML descargado correctamente");
      else toast.error(typeof res.mensaje === "string" ? res.mensaje : "Error al reintentar descarga");
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    } finally {
      setActionLoading(null);
    }
  };

  const anularUno = async (id: number, creds: SiiCredentials, causa: number) => {
    setActionLoading(id);
    try {
      const res = await api<Record<string, unknown>>(`/documentos/${id}/anular`, {
        method: "POST",
        body: JSON.stringify({
          causa,
          rutSii: creds.rutSii,
          claveSii: creds.claveSii,
          claveCertificado: creds.claveCertificado,
        }),
      });
      if (res.success) {
        toast.success(res.folio ? `Nota de Crédito Folio ${res.folio} emitida. Documento anulado.` : "Documento anulado");
      } else {
        toast.error(typeof res.mensaje === "string" ? res.mensaje : "Error al anular");
      }
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarcarAnuladoExterno = async (id: number, folio: number | null) => {
    const folioStr = folio ? `Folio ${folio}` : "este documento";
    if (!confirm(`¿Marcar ${folioStr} como ANULADO en Folio Listo?\n\nUsa esta opción si la anulación o Nota de Crédito ya fue realizada por fuera directamente en el SII.`)) {
      return;
    }
    setActionLoading(id);
    try {
      const res = await api<Record<string, unknown>>(`/documentos/${id}/marcar-anulado`, {
        method: "POST",
        body: JSON.stringify({ motivo: "Anulación directa en portal SII" }),
      });
      if (res.success) {
        toast.success(`Documento Folio ${folio} marcado como anulado`);
        load();
      } else {
        toast.error("Error al marcar como anulado");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    } finally {
      setActionLoading(null);
    }
  };

  const anularSeleccionados = async (creds: SiiCredentials, causa: number) => {
    if (selected.size === 0) return;
    setAnularLoading(true);
    let ok = 0;
    let fail = 0;
    for (const id of selected) {
      try {
        const res = await api<Record<string, unknown>>(`/documentos/${id}/anular`, {
          method: "POST",
          body: JSON.stringify({
            causa,
            rutSii: creds.rutSii,
            claveSii: creds.claveSii,
            claveCertificado: creds.claveCertificado,
          }),
        });
        if (res.success) ok++;
        else fail++;
      } catch {
        fail++;
      }
    }
    setAnularLoading(false);
    setSelected(new Set());
    if (ok > 0) toast.success(`${ok} documento(s) anulados`);
    if (fail > 0) toast.error(`${fail} documento(s) no se pudieron anular`);
    load();
  };

  const handleClaveSubmit = (creds: SiiCredentials) => {
    setClaveModalOpen(false);
    if (pendingAction?.type === "reintentar") {
      reintentarHtml(pendingAction.ids[0], creds);
    }
    setPendingAction(null);
  };

  const handleAnularSubmit = (creds: SiiCredentials, causa: number) => {
    setClaveModalOpen(false);
    if (pendingAction?.type === "anular") {
      if (pendingAction.ids.length === 1) anularUno(pendingAction.ids[0], creds, causa);
      else anularSeleccionados(creds, causa);
    }
    setPendingAction(null);
  };

  const verDocumento = (id: number) => {
    const token = getToken();
    const url = `/api/documentos/${id}/descargar?token=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank");
  };

  const descargarSeleccionadosZip = async () => {
    if (selected.size === 0) return;
    setDownloadingZip(true);
    const toastId = toast.loading(`Preparando ZIP con ${selected.size} documento(s)...`);
    const token = getToken();
    try {
      const zip = new JSZip();
      let okCount = 0;
      for (const id of selected) {
        try {
          const doc = documentos.find((d) => d.id === id);
          const res = await fetch(`/api/documentos/${id}/descargar`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) continue;
          const blob = await res.blob();
          const isHtml = blob.type.includes("html");
          const ext = isHtml ? "html" : "pdf";
          const filename = doc ? `${tipoLabel(doc.tipoDte).replace(/\s+/g, "_")}_folio_${doc.folio || doc.id}.${ext}` : `documento_${id}.${ext}`;
          zip.file(filename, blob);
          okCount++;
        } catch (e) {
          console.error(`Error al empaquetar doc ${id}:`, e);
        }
      }

      if (okCount === 0) {
        toast.error("No se pudo obtener ningún documento para el ZIP", { id: toastId });
        return;
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `documentos_emitidos_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);

      toast.success(`${okCount} documento(s) descargados en ZIP`, { id: toastId });
    } catch {
      toast.error("Error al generar archivo ZIP", { id: toastId });
    } finally {
      setDownloadingZip(false);
    }
  };

  const renderTable = () => {
    if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
    if (documentos.length === 0) return <p className="text-sm text-muted-foreground text-center py-8">No hay documentos emitidos.</p>;

    const isBheTab = activeTab === "bhe";
    const isBeTab = activeTab === "be";
    const isFacturaTab = activeTab === "factura";
    const isNcTab = activeTab === "nota-credito";

    const allSelected = documentos.length > 0 && selected.size === documentos.length;

    const emisorIndices = new Map<string, number>();
    let groupCounter = 0;
    for (const doc of documentos) {
      if (!emisorIndices.has(doc.rutEmisor)) {
        emisorIndices.set(doc.rutEmisor, groupCounter++);
      }
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              {documentos.length > 0 && (
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 accent-teal-600 focus:ring-teal-500 cursor-pointer"
                />
              )}
            </TableHead>
            <TableHead>Fecha</TableHead>
            <TableHead className="text-orange-500 font-medium">RUT Emisor</TableHead>
            <TableHead>Folio</TableHead>
            {(isBeTab || isFacturaTab) && <TableHead>Tipo DTE</TableHead>}
            {isNcTab && <TableHead>Doc Ref</TableHead>}
            <TableHead>{isFacturaTab ? "Receptor / Razón Social" : "Receptor"}</TableHead>
            {isBheTab && <TableHead>Retención</TableHead>}
            {isFacturaTab ? (
              <>
                <TableHead className="text-right">Neto</TableHead>
                <TableHead className="text-right">IVA</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </>
            ) : (
              <TableHead className="text-right">Monto</TableHead>
            )}
            <TableHead>Estado</TableHead>
            <TableHead className="text-left whitespace-nowrap w-44">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documentos.map((doc) => {
            const isEmitido = doc.estado === "EMITIDO" || doc.estado === "EMITIDO_SIN_PDF" || doc.estado === "EMITIDO_SIN_HTML";
            const isBheAnul = isBheTab && isEmitido && doc.tipoDte === 69 && isBheAnulable(doc.fechaEmision, doc.tipoDte);
            const isBheFueraPlazo = isBheTab && isEmitido && doc.tipoDte === 69 && !isBheAnulable(doc.fechaEmision, doc.tipoDte);
            const isDteAnul = (isBeTab || isFacturaTab) && isEmitido && (doc.tipoDte === 39 || doc.tipoDte === 41 || doc.tipoDte === 33 || doc.tipoDte === 34);
            const isAnulable = isBheAnul || isDteAnul;
            const groupIdx = emisorIndices.get(doc.rutEmisor) ?? 0;
            const isEvenGroup = groupIdx % 2 === 0;
            return (
              <TableRow
                key={doc.id}
                className={`${isEvenGroup ? "bg-orange-50/40 dark:bg-orange-950/20" : ""} cursor-pointer`}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.closest("button") || target.closest("td:last-child") || target.closest("input")) return;
                  toggleSelect(doc.id);
                }}
              >
                <TableCell className="w-10">
                  <input
                    type="checkbox"
                    checked={selected.has(doc.id)}
                    onChange={() => toggleSelect(doc.id)}
                    className="h-4 w-4 rounded border-gray-300 accent-teal-600 focus:ring-teal-500 cursor-pointer"
                  />
                  {isBheFueraPlazo && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex ml-1"><Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" /></span>
                      </TooltipTrigger>
                      <TooltipContent>
                        El plazo para anular esta boleta por internet ya expiró. Revísalo directamente en el SII.
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                  {new Date(doc.fechaEmision).toLocaleDateString("es-CL")}
                </TableCell>
                <TableCell className="font-medium text-sm whitespace-nowrap">{doc.rutEmisor ?? "—"}</TableCell>
                <TableCell className="font-mono font-medium">{doc.folio ?? "—"}</TableCell>
                {(isBeTab || isFacturaTab) && (
                  <TableCell className="whitespace-nowrap">{tipoBadge(doc.tipoDte)}</TableCell>
                )}
                {isNcTab && (
                  <TableCell className="text-xs text-muted-foreground font-mono">
                    {doc.documentoRefId ? `Ref #${doc.documentoRefId}` : "—"}
                  </TableCell>
                )}
                <TableCell className="max-w-[240px]">
                  <div className="font-medium text-sm truncate" title={doc.receptorNombre || undefined}>
                    {doc.receptorNombre || (doc.tipoDte === 39 || doc.tipoDte === 41 ? "Consumidor Final" : "—")}
                  </div>
                  <div className="flex flex-col text-xs text-muted-foreground mt-0.5">
                    {doc.receptorRut && doc.receptorRut !== "66666666-6" && (
                      <span className="font-mono">{doc.receptorRut}</span>
                    )}
                    {doc.descripcionServicio && (
                      <span className="truncate text-muted-foreground/80 italic text-[11px]" title={doc.descripcionServicio}>
                        {doc.descripcionServicio}
                      </span>
                    )}
                  </div>
                </TableCell>
                {isBheTab && (
                  <TableCell className="text-xs">{retencionLabel(doc.tipoRetencion)}</TableCell>
                )}
                {isFacturaTab ? (
                  <>
                    <TableCell className="text-right whitespace-nowrap text-xs text-muted-foreground">
                      ${(doc.montoNeto ?? doc.montoTotal).toLocaleString("es-CL")}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap text-xs text-muted-foreground">
                      ${(doc.montoIva ?? 0).toLocaleString("es-CL")}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap font-medium">
                      ${(doc.montoTotal ?? 0).toLocaleString("es-CL")}
                    </TableCell>
                  </>
                ) : (
                  <TableCell className="text-right whitespace-nowrap font-medium">
                    ${(doc.montoTotal ?? 0).toLocaleString("es-CL")}
                  </TableCell>
                )}
                <TableCell>{estadoBadge(doc)}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <div className="flex gap-2 justify-start items-center">
                    {(doc.estado === "EMITIDO" || doc.estado === "EMITIDO_SIN_PDF" || doc.estado === "EMITIDO_SIN_HTML" || doc.estado === "ANULADO") && (
                      <>
                        <button
                          type="button"
                          onClick={() => verDocumento(doc.id)}
                          title="Ver documento (abrir en pestaña nueva)"
                          className="inline-flex items-center h-7 px-2.5 gap-1.5 rounded-md border border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100 hover:border-teal-400 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-200 text-xs font-medium shadow-xs transition-colors cursor-pointer"
                        >
                          <Eye className="h-3.5 w-3.5 text-teal-700 dark:text-teal-300" />
                          <span>Ver</span>
                        </button>
                        {doc.notaCredito && (
                          <button
                            type="button"
                            onClick={() => verDocumento(doc.notaCredito!.id)}
                            title={`Ver Nota de Crédito Folio ${doc.notaCredito.folio} que anula este documento`}
                            className="inline-flex items-center h-7 px-2.5 gap-1.5 rounded-md border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 hover:border-rose-400 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200 text-xs font-medium shadow-xs transition-colors cursor-pointer"
                          >
                            <FileText className="h-3.5 w-3.5 text-rose-700 dark:text-rose-300" />
                            <span>Ver NC #{doc.notaCredito.folio}</span>
                          </button>
                        )}
                        {doc.documentoRef && (
                          <button
                            type="button"
                            onClick={() => verDocumento(doc.documentoRef!.id)}
                            title={`Ver documento original Folio ${doc.documentoRef.folio}`}
                            className="inline-flex items-center h-7 px-2.5 gap-1.5 rounded-md border border-slate-300 bg-slate-50 text-slate-800 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200 text-xs font-medium shadow-xs transition-colors cursor-pointer"
                          >
                            <FileText className="h-3.5 w-3.5 text-slate-600 dark:text-slate-400" />
                            <span>Doc Ref #{doc.documentoRef.folio}</span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setShareDoc(doc)}
                          title="Compartir documento (WhatsApp, enlace o apps)"
                          className="inline-flex items-center h-7 px-2.5 gap-1.5 rounded-md border border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100 hover:border-sky-400 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200 text-xs font-medium shadow-xs transition-colors cursor-pointer"
                        >
                          <Share2 className="h-3.5 w-3.5 text-sky-700 dark:text-sky-300" />
                          <span>Compartir</span>
                        </button>
                      </>
                    )}
                    {(doc.estado === "EMITIDO_SIN_HTML" || doc.estado === "EMITIDO_SIN_PDF") && (
                      <button
                        type="button"
                        onClick={() => reintentarHtml(doc.id)}
                        disabled={actionLoading === doc.id}
                        title="Reintentar descarga"
                        className="inline-flex items-center justify-center h-7 px-2 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 text-xs transition-colors cursor-pointer disabled:opacity-50"
                      >
                        {actionLoading === doc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                      </button>
                    )}
                    {isAnulable && (
                      <button
                        type="button"
                        onClick={() => { setPendingAction({ type: "anular", ids: [doc.id] }); setClaveModalOpen(true); }}
                        disabled={actionLoading === doc.id}
                        title={doc.tipoDte === 69 ? "Anular BHE" : "Anular con Nota de Crédito (DTE 61)"}
                        className="inline-flex items-center h-7 px-2.5 gap-1.5 rounded-md border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 hover:border-rose-400 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200 text-xs font-medium shadow-xs transition-colors cursor-pointer disabled:opacity-50"
                      >
                        {actionLoading === doc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 text-rose-600 dark:text-rose-400" />}
                        <span>Anular</span>
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  const selectedAnulables = Array.from(selected).filter((id) => {
    const d = documentos.find((doc) => doc.id === id);
    if (!d) return false;
    const isEmitido = d.estado === "EMITIDO" || d.estado === "EMITIDO_SIN_PDF" || d.estado === "EMITIDO_SIN_HTML";
    if (d.tipoDte === 69) return isEmitido && isBheAnulable(d.fechaEmision, d.tipoDte);
    return isEmitido && (d.tipoDte === 39 || d.tipoDte === 41 || d.tipoDte === 33 || d.tipoDte === 34);
  });

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {TAB_CONFIG.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>{t.label}</TabsTrigger>
          ))}
        </TabsList>

        {TAB_CONFIG.map((t) => (
          <TabsContent key={t.value} value={t.value}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3 space-y-0">
                <CardTitle>{t.label}</CardTitle>
                <div className="flex items-center gap-2">
                  {selected.size > 0 && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={descargarSeleccionadosZip}
                      disabled={downloadingZip}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 shadow-sm"
                    >
                      {downloadingZip ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                      Descargar ZIP ({selected.size})
                    </Button>
                  )}
                  {selectedAnulables.length > 0 && t.value !== "nota-credito" && (
                    <button
                      type="button"
                      onClick={() => { setPendingAction({ type: "anular", ids: selectedAnulables }); setClaveModalOpen(true); }}
                      disabled={anularLoading}
                      className="inline-flex items-center justify-center h-9 px-3 rounded-md text-xs font-medium gap-1.5 shadow-xs bg-rose-600 hover:bg-rose-700 text-white border border-rose-700 cursor-pointer transition-colors disabled:opacity-50"
                    >
                      {anularLoading ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="mr-1.5 h-3.5 w-3.5 text-white" />
                      )}
                      <span>Anular selección ({selectedAnulables.length})</span>
                    </button>
                  )}
                  <Button variant="outline" size="sm" onClick={load} disabled={loading} title="Actualizar">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {renderTable()}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <ClaveSiiModal
        open={claveModalOpen}
        onClose={() => { setClaveModalOpen(false); setPendingAction(null); }}
        onSubmit={handleClaveSubmit}
        onSubmitWithCausa={handleAnularSubmit}
        anulacionMode={pendingAction?.type === "anular"}
        documentCount={pendingAction?.ids.length ?? 1}
        requiresCertificado={
          pendingAction?.type === "anular" &&
          pendingAction.ids.some((id) => {
            const d = documentos.find((doc) => doc.id === id);
            return d ? (d.tipoDte === 33 || d.tipoDte === 34 || d.tipoDte === 39 || d.tipoDte === 41) : false;
          })
        }
        onMarcarAnuladoManual={
          pendingAction?.type === "anular" &&
          pendingAction.ids.length === 1 &&
          (() => {
            const d = documentos.find((doc) => doc.id === pendingAction.ids[0]);
            return d && (d.tipoDte === 33 || d.tipoDte === 34 || d.tipoDte === 39 || d.tipoDte === 41)
              ? () => handleMarcarAnuladoExterno(d.id, d.folio)
              : undefined;
          })()
        }
      />

      <CompartirModal
        open={!!shareDoc}
        onClose={() => setShareDoc(null)}
        doc={shareDoc}
      />
    </div>
  );
}
